import { useEffect } from 'react';

import styled from '@emotion/styled';
import { useFormik } from 'formik';
import toast from 'react-hot-toast';
import * as Yup from 'yup';

import { PrimaryBlackButton } from '../../../components/Button/Buttons';
import { Card, CardTitle, Hint, Input, Scroll, SimpleTable } from '../../../components/Layout/PagePieces';
import useVtpassAlertSettings from '../../../hooks/dashboard/ops/useVtpassAlertSettings';
import useVtpassBalanceStatus from '../../../hooks/dashboard/ops/useVtpassBalanceStatus';
import { AlertSettingKey } from '../../../models/ops/vtpass.model';
import { formatNaira, tableFormatDateTimeNG } from '../../../utils/utils';

const LABELS: Record<AlertSettingKey, string> = {
  VTPASS_ALERT_THRESHOLD: 'Alert below (₦)',
  VTPASS_CRITICAL_THRESHOLD: 'Critical below (₦)',
  VTPASS_ALERT_HOURS_LEFT: 'Or when it lasts under (hours)',
  VTPASS_ALERT_REPEAT_MINUTES: 'Remind every (minutes)',
};
const KEYS = Object.keys(LABELS) as AlertSettingKey[];
const NAIRA_KEYS: AlertSettingKey[] = ['VTPASS_ALERT_THRESHOLD', 'VTPASS_CRITICAL_THRESHOLD'];

const schema = Yup.object({
  VTPASS_ALERT_THRESHOLD: Yup.number().integer().min(100, 'At least ₦100').required('Required'),
  VTPASS_CRITICAL_THRESHOLD: Yup.number()
    .integer()
    .min(0)
    .required('Required')
    .test('below', 'Must be below the alert level', function (v) {
      return v == null || v < Number(this.parent.VTPASS_ALERT_THRESHOLD);
    }),
  VTPASS_ALERT_HOURS_LEFT: Yup.number().integer().min(1).max(168, 'At most 168').required('Required'),
  VTPASS_ALERT_REPEAT_MINUTES: Yup.number().integer().min(10, 'At least 10').max(1440, 'At most 1440').required('Required'),
});

// The VTpass wallet alert levels (ops spec V). Saved values are used from the
// next 10-minute check on; the settings file only holds the defaults.
const VtpassAlertsCard = () => {
  const { settings, saving, fetchSettings, save } = useVtpassAlertSettings();
  const { status, fetchStatus } = useVtpassBalanceStatus();

  useEffect(() => {
    fetchSettings();
    fetchStatus();
    // eslint-disable-next-line
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: schema,
    initialValues: Object.fromEntries(KEYS.map((k) => [k, settings?.values[k] ?? 0])) as Record<AlertSettingKey, number>,
    onSubmit: (v) => {
      const changed = Object.fromEntries(KEYS.filter((k) => Number(v[k]) !== settings?.values[k]).map((k) => [k, Number(v[k])]));
      if (!Object.keys(changed).length) {
        toast('Nothing changed');
        return;
      }
      save(changed, () => toast.success('Saved. The next balance check uses these.'));
    },
  });

  return (
    <Card>
      <CardTitle>VTpass wallet alerts</CardTitle>
      <Hint>
        {status?.balance != null
          ? `Balance ${formatNaira(Number(status.balance))} at the last check${status.checked_at ? ` (${tableFormatDateTimeNG(status.checked_at)})` : ''}. `
          : ''}
        The alert level also rises with how fast we spend and with schedules due in the next 24 hours.
        {!status?.alerts_enabled && ' WhatsApp alerts are off until OPS_ALERT_CHAT_IDS is set.'}
        {settings?.updated_at ? ` Last changed ${tableFormatDateTimeNG(settings.updated_at)} by ${settings.updated_by}.` : ''}
      </Hint>
      <form onSubmit={formik.handleSubmit}>
        <Grid>
          {KEYS.map((key) => (
            <Field key={key}>
              <span>{LABELS[key]}</span>
              <Input type='number' name={key} value={formik.values[key]} onChange={formik.handleChange} min={0} />
              {formik.errors[key] ? (
                <ErrorText>{String(formik.errors[key])}</ErrorText>
              ) : (
                <small>
                  default {NAIRA_KEYS.includes(key) ? formatNaira(settings?.file_defaults[key] ?? 0) : settings?.file_defaults[key]}
                </small>
              )}
            </Field>
          ))}
        </Grid>
        <PrimaryBlackButton type='submit' disabled={saving}>
          {saving ? 'Saving…' : 'Save alert levels'}
        </PrimaryBlackButton>
      </form>
      {!!settings?.history.length && (
        <Scroll>
          <SimpleTable>
            <thead>
              <tr>
                <th>Changed</th>
                <th>Setting</th>
                <th className='num'>Value</th>
                <th>By</th>
              </tr>
            </thead>
            <tbody>
              {settings.history.map((h, i) => (
                <tr key={i}>
                  <td>{tableFormatDateTimeNG(h.changed_at)}</td>
                  <td>{LABELS[h.key] ?? h.key}</td>
                  <td className='num'>{NAIRA_KEYS.includes(h.key) ? formatNaira(h.value) : h.value}</td>
                  <td>{h.changed_by}</td>
                </tr>
              ))}
            </tbody>
          </SimpleTable>
        </Scroll>
      )}
    </Card>
  );
};

export default VtpassAlertsCard;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.palette.grayTextColor};

  small {
    font-size: 11px;
  }
`;

const ErrorText = styled.span`
  color: ${({ theme }) => theme.palette.errorRed};
  font-size: 12px;
`;
